import React, { useCallback, useEffect, useMemo, useState } from 'react';
// Enhanced Multi-Grid Editor - Full GridEditor functionality with multi-grid tabs
import { useNavigate } from 'react-router-dom';
import { Grid as GridIcon, Save, Edit, Trash, Plus, Check, X, AlertTriangle, Clock, Users, Search, RefreshCw, ArrowLeft, ArrowRight, ChevronDown, Info, Layers, FileText, Download, UploadCloud, TestTube, Calendar, Sidebar as SidebarIcon, PanelLeft, PanelRight, Heart, MoreHorizontal, MessageSquare } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { displayLearningArea } from '../../constants/learningAreas';

// Use stabilized contexts
import { useStabilizedGrid, useStabilizedAllocation } from '../../contexts/StabilizedContexts';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

// Multi-grid functionality
import { MultiGridProvider } from '../../contexts/MultiGridContext';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { MultiGridTabs } from '../../components/grid/MultiGridTabs';
import { CombinedGridView } from '../../components/grid/CombinedGridView';

// Original grid components
import StaffAllocationGrid from '../../components/grid/StaffAllocationGrid';
import TeacherLoadPanel from '../../components/grid/TeacherLoadPanel';
import GridSettingsModal from '../../components/grid/GridSettingsModal';

// Types and utilities
import { StaffAllocationGrid as StaffGrid, ClassAllocation, LineDefinition, StudyPeriodDefinition } from '../../types/grid';
import { TeachingIntention } from '../../types/intentions';
import { CurriculumSubject, findPerfectMatches, findPersonalPreferenceMatches, findCurriculumPreferenceMatches, findAllMatches, calculatePreferenceStrength, buildPriorTeachingIndex } from '../../utils/preferenceMatching';
import AdditionalCommentsModal from '../../components/intentions/AdditionalCommentsModal';
import { intentionsService } from '../../services/intentionsService';
import { detectConflicts, generateConflictSummary } from '../../utils/conflictDetection';
import { supabase } from '../../supabase/config';
import { LineSubjectConfig, SubjectLineAvailability, createSubjectAvailabilityMap } from '../../types/lineSubject';
import { exportGridForKaren } from '../../utils/karenGridExport';
import { availabilityFromClasses, hasClassLineAvailability } from '../../utils/gridSetup';

/**
 * The school's auto-assign priority order, in the coordinator's own words.
 * Must stay in step with assignmentPriorityTier in src/utils/preferenceMatching.ts.
 */
/**
 * What a teacher actually wrote on their intentions form — their own words, in
 * their own ranking, with nothing inferred.
 *
 * Kept deliberately separate from PIT's computed matches. The staff preferences
 * drawer previously showed ONLY the computed matches, under the heading "Teaching
 * Preferences", so a subject the engine had merely inferred from a broad learning
 * area was indistinguishable from one the teacher had asked for by name. A
 * coordinator checking an auto-assign result against this panel was checking the
 * engine against itself (issue #182).
 *
 * Reads the structured `preferences` column, which is what the submission form
 * writes. Spreadsheet-imported rows have no such column, so it falls back to the
 * coded `teachingPreferences` array — see [[Teaching-Intentions-Data-Shapes]].
 */
const StatedNominations: React.FC<{ intention: any }> = ({ intention }) => {
  if (!intention) {
    return (
      <div className="text-sm text-ink-500">
        No teaching intention submitted for this year.
      </div>
    );
  }

  const prefs = intention.preferences || {};
  const asList = (v: any) => (Array.isArray(v) ? v : []);
  const byRank = (a: any, b: any) => (a?.rank ?? 999) - (b?.rank ?? 999);

  const subjects = asList(prefs.subjects).slice().sort(byRank);
  const ruah = asList(prefs.ruahSubjects).slice().sort(byRank);
  const areas = asList(prefs.curriculumAreas).slice().sort(byRank);

  // Imported rows carry the coded array instead of the structured column.
  const legacy =
    subjects.length || ruah.length || areas.length
      ? []
      : asList(intention.teachingPreferences).slice().sort(byRank);

  const nothing =
    !subjects.length && !ruah.length && !areas.length && !legacy.length;

  const Row = ({ label, items }: { label: string; items: string[] }) =>
    items.length === 0 ? null : (
      <div className="mb-1">
        <span className="text-xs text-ink-500">{label}: </span>
        <span className="text-xs text-ink-700">{items.join(' · ')}</span>
      </div>
    );

  return (
    <div>
      <div className="text-sm font-medium text-ink-700 mb-2">
        They asked for
      </div>
      {nothing ? (
        <div className="text-xs text-ink-500">
          Nothing specific — no subjects or learning areas were nominated.
        </div>
      ) : (
        <>
          <Row
            label="Subjects"
            items={subjects.map((s: any, i: number) => `${s?.rank ?? i + 1}. ${s?.subjectName || s?.subjectId}`)}
          />
          <Row
            label="RUAH"
            items={ruah.map((s: any, i: number) => `${s?.rank ?? i + 1}. ${s?.subjectName || s?.subjectId}`)}
          />
          <Row
            label="Learning areas"
            items={areas.map((a: any, i: number) => `${a?.rank ?? i + 1}. ${a?.areaName}`)}
          />
          <Row
            label="Nominated"
            items={legacy.map((p: any, i: number) => `${p?.rank ?? i + 1}. ${p?.specificSubject || p?.learningArea}`)}
          />
          {subjects.length === 0 && ruah.length === 0 && areas.length > 0 && (
            <div className="mt-1 text-xs text-warn-fg">
              Named no specific subjects — only learning areas.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PRIORITY_TIER_LABELS = {
  1: 'Teacher asked for it, leader nominated them, and they taught it last year',
  2: 'Teacher asked for it and leader nominated them',
  3: 'Teacher asked for this subject',
  4: 'Leader nominated them for this subject',
  5: 'Teacher asked for this learning area, and taught it last year',
  6: 'Teacher asked for this learning area',
} as const;
// Removed flag context and error boundary for stability - comprehensive fix FINAL

const EnhancedMultiGridEditorContent: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { setSidebarVisible, sidebarVisible } = useLayout();
  
  // Multi-grid context
  const {
    allGrids,
    currentGrid,
    currentGridId,
    setCurrentGridId,
    multiGridView,
    createGrid,
    updateGrid,
    gridTypes,
    isLoading: multiGridLoading
  } = useMultiGrid();

  // Original stabilized contexts
  const {
    activeGrid,
    setActiveGrid,
    grids,
    updateGrid: updateStabilizedGrid,
    refetchGrid,
    loading: gridLoading
  } = useStabilizedGrid();

  const { staff } = useData();

  // Academic year for year display
  let workingYear: number;
  try {
    const academicYearContext = useAcademicYear();
    workingYear = academicYearContext.workingYear;
  } catch (e) {
    workingYear = new Date().getFullYear();
  }

  // Intentions are fetched for the year of the grid actually on screen, so the
  // teacher panels can never describe a different year's submissions than the
  // classes beside them (issue #209). The effect below keeps that grid in step
  // with the working year, so in practice these agree (issue #211).
  const gridYear: number | null =
    ((activeGrid?.year ?? currentGrid?.year) ?? null) as number | null;
  const planningYear = gridYear ?? workingYear;

  const {
    assignTeacher,
    assignTeacherMultiGrid,
    batchAssignTeachers,
    getTeacherSemesterLoad
  } = useStabilizedAllocation();

  // All original GridEditor state
  const [showGridSettings, setShowGridSettings] = useState(false);
  const [showConflicts, setShowConflicts] = useState(false);
  const [showLoadsDrawer, setShowLoadsDrawer] = useState(false);

  // Debug wrapper to track when drawer state changes
  const debugSetShowLoadsDrawer = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(showLoadsDrawer) : value;
    console.log(`🔍 Teacher Loads Drawer state change: ${showLoadsDrawer} -> ${newValue}`, new Error().stack);
    setShowLoadsDrawer(value);
  }, [showLoadsDrawer]);

  // Monitor state value changes
  useEffect(() => {
    console.log(`🔍 showLoadsDrawer value is now: ${showLoadsDrawer}`);
  }, [showLoadsDrawer]);
  const [showPreferencesDrawer, setShowPreferencesDrawer] = useState(false);

  // Debug staff preferences drawer state changes
  const debugSetShowPreferencesDrawer = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(showPreferencesDrawer) : value;
    console.log(`🔍 Staff Preferences Drawer state change: ${showPreferencesDrawer} -> ${newValue}`, new Error().stack);
    setShowPreferencesDrawer(value);
  }, [showPreferencesDrawer]);

  // Monitor preferences drawer state value changes
  useEffect(() => {
    console.log(`🔍 showPreferencesDrawer value is now: ${showPreferencesDrawer}`);
  }, [showPreferencesDrawer]);

  // Simple defensive handler to ensure drawer can always be closed
  const handleCloseLoadsDrawer = useCallback(() => {
    debugSetShowLoadsDrawer(false);
  }, [debugSetShowLoadsDrawer]);
  const [preferencesSearchQuery, setPreferencesSearchQuery] = useState('');
  const [expandedStaffPreferences, setExpandedStaffPreferences] = useState<Set<string>>(new Set());
  const [showCreateGrid, setShowCreateGrid] = useState(false);
  const [newGridName, setNewGridName] = useState('');
  const [newGridMinutes, setNewGridMinutes] = useState(70);
  const [newGridStudyPeriods, setNewGridStudyPeriods] = useState([
    { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
    { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
  ]);
  const [lineInputValues, setLineInputValues] = useState<{ [key: number]: string }>({});
  const [activeSemester, setActiveSemester] = useState<'both' | 'semester1' | 'semester2'>('both');

  // Staff filtering state
  const [selectedStaffFilter, setSelectedStaffFilter] = useState<string[]>([]);
  const [showStaffFilterDropdown, setShowStaffFilterDropdown] = useState(false);
  const [staffFilterSearchQuery, setStaffFilterSearchQuery] = useState('');

  // Preference matching data
  const [teachingIntentions, setTeachingIntentions] = useState<TeachingIntention[]>([]);
  const [curriculumSubjects, setCurriculumSubjects] = useState<CurriculumSubject[]>([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);

  // Additional comments modal state
  const [commentsModal, setCommentsModal] = useState<{
    isOpen: boolean;
    staffName: string;
    staffInitial: string;
    comments: string;
  }>({
    isOpen: false,
    staffName: '',
    staffInitial: '',
    comments: ''
  });

  // Auto-assignment state
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [showAutoAssignConfirm, setShowAutoAssignConfirm] = useState(false);
  const [allMatches, setAllMatches] = useState<any[]>([]);
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());
  const [assignmentType, setAssignmentType] = useState<'all' | 'perfect' | 'personal' | 'curriculum'>('all');
  const [showAutoAssignDropdown, setShowAutoAssignDropdown] = useState(false);
  const [showMoreActionsDropdown, setShowMoreActionsDropdown] = useState(false);
  const [preventPreferenceReload, setPreventPreferenceReload] = useState(false);

  // Line-Subject availability state
  const [subjectLineAvailability, setSubjectLineAvailability] = useState<SubjectLineAvailability>({});

  // Sync multi-grid current grid with stabilized grid
  useEffect(() => {
    if (currentGrid && currentGrid.id !== activeGrid?.id) {
      setActiveGrid(currentGrid.id);
    }
  }, [currentGrid, activeGrid, setActiveGrid]);

  // Hide sidebar on mount for full-width view
  useEffect(() => {
    setSidebarVisible(false);
    return () => {
      setSidebarVisible(true);
    };
  }, [setSidebarVisible]);

  // The working year is the master; the grid selection follows it.
  //
  // The shared grid context defaults the active grid to the alphabetically first
  // grid across ALL years (StabilizedContexts: `gridsData[0]` after a sort by
  // name), so the editor can open on the 2026 Main Grid while the user has 2027
  // selected — and then serve 2026 intentions against it. Correct the selection
  // rather than the year: pulling the year across to the grid would silently
  // rewrite the user's stored working year and snap them back every visit.
  useEffect(() => {
    if (!activeGrid || !grids || grids.length === 0) return;
    if (activeGrid.year == null || activeGrid.year === workingYear) return;
    const target =
      grids.find(g => g.year === workingYear && g.name === activeGrid.name) ||
      grids.find(g => g.year === workingYear);
    if (target && target.id !== activeGrid.id) {
      console.log(
        `🔍 EnhancedMultiGridEditor: active grid was ${activeGrid.name} (${activeGrid.year}) but ` +
        `the working year is ${workingYear} — switching to ${target.name} (${target.year})`
      );
      setActiveGrid(target.id);
    }
  }, [activeGrid, grids, workingYear, setActiveGrid]);

  // Load teaching intentions on mount, and again whenever the planning year
  // changes — the intentions fetched are for that year (issue #165).
  useEffect(() => {
    loadPreferenceData();
    // loadPreferenceData is intentionally omitted: it is re-created whenever
    // activeGrid or the loading flag changes, which would refetch on every cell edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningYear]);

  // Load preferences data when needed
  const loadPreferenceData = useCallback(async () => {
    if (preferencesLoading || preventPreferenceReload) return;

    console.log(`🔍 EnhancedMultiGridEditor: Starting to load preference data...`);
    setPreferencesLoading(true);
    try {
      // Intentions belong to the year being planned, not to "next calendar year".
      // The old `getFullYear() + 1` happened to be right while planning was done in
      // the preceding year, and would silently return zero rows once the calendar
      // rolled over into the year still being built (issue #165).
      console.log(`🔍 EnhancedMultiGridEditor: Loading intentions for year ${planningYear}`);
      const intentions = await intentionsService.getIntentionsByYear(planningYear);

      console.log(`🔍 EnhancedMultiGridEditor: Loaded ${intentions.length} teaching intentions`);
      console.log(`🔍 EnhancedMultiGridEditor: Sample intentions:`, intentions.slice(0, 3).map(i => ({
        staffId: i.staffId,
        staffName: i.staffName,
        preferences: i.teachingPreferences?.length || 0,
        samplePreference: i.teachingPreferences?.[0] ? {
          specificSubject: i.teachingPreferences[0].specificSubject,
          learningArea: i.teachingPreferences[0].learningArea,
          rank: i.teachingPreferences[0].rank
        } : null
      })));

      setTeachingIntentions(intentions);
      
      const subjects: CurriculumSubject[] = [];
      
      // Load curriculum preferences from Supabase if activeGrid exists
      console.log(`🔍 EnhancedMultiGridEditor: Loading curriculum preferences for grid:`, activeGrid?.id);
      if (activeGrid) {
        try {
          const { data: curriculumPrefs, error } = await supabase
            .from('curriculum_preferences')
            .select('*')
            .eq('grid_id', activeGrid.id);

          console.log(`🔍 EnhancedMultiGridEditor: Curriculum preferences query result:`, { data: curriculumPrefs, error });

          if (error) {
            console.warn('⚠️ Database query failed for curriculum preferences, will use fallback');
          } else if (curriculumPrefs && curriculumPrefs.length > 0) {
            console.log(`✅ Loaded ${curriculumPrefs.length} curriculum preferences from database`);
            subjects.push(...curriculumPrefs.map(pref => ({
              subjectId: pref.subject_id,
              subjectName: pref.subject_name,
              teacherPreferences: (pref.teacher_preferences || []).map((tp: any) => ({
                teacherId: tp.teacherId || '',
                teacherCode: tp.teacherCode || '',
                teacherName: tp.teacherName || '',
                preferenceLevel: tp.preferenceLevel || 'medium',
                addedBy: tp.addedBy,
                addedAt: tp.addedAt
              }))
            })));
          } else {
            console.log('📁 No curriculum preferences in database, loading from import file...');
          }
        } catch (error) {
          console.warn('⚠️ Error querying curriculum preferences:', error);
        }
      }

      // If the database has no curriculum subjects, show none rather than seeding
      // sample data. Subjects come from the school's own preferences/assignments.
      setCurriculumSubjects(subjects);

      // Enhanced debugging for curriculum subjects
      console.log(`🔍 EnhancedMultiGridEditor: Final curriculum subjects loaded:`, {
        totalCount: subjects.length,
        hasDatabase: activeGrid ? 'attempted' : 'skipped (no active grid)',
        hasImportFile: subjects.length > 0,
        sampleSubjects: subjects.slice(0, 3).map(s => ({
          subjectId: s.subjectId,
          subjectName: s.subjectName,
          teacherCount: s.teacherPreferences?.length || 0,
          sampleTeacher: s.teacherPreferences?.[0] ? {
            code: s.teacherPreferences[0].teacherCode,
            name: s.teacherPreferences[0].teacherName,
            level: s.teacherPreferences[0].preferenceLevel
          } : null
        }))
      });

      console.log(`🔍 EnhancedMultiGridEditor: Preference data loading completed successfully`);
    } catch (error) {
      console.error('❌ EnhancedMultiGridEditor: Critical error loading preference data:', error);
      addToast('Failed to load preference data. Auto-assign may not work correctly.', 'error');

      // Set empty arrays to prevent undefined issues
      setTeachingIntentions([]);
      setCurriculumSubjects([]);
    } finally {
      setPreferencesLoading(false);
    }
  }, [preferencesLoading, preventPreferenceReload, activeGrid, planningYear]);

  // Load subject-line availability. Prefer the per-class `allowedLineIds` on the
  // grid (the source of truth for grids built/edited after that change); fall back
  // to the legacy line_subject_config table for older grids that predate it.
  useEffect(() => {
    if (!activeGrid?.id) return;

    // Source of truth: availability stored on the classes themselves.
    if (hasClassLineAvailability(activeGrid.classes)) {
      setSubjectLineAvailability(availabilityFromClasses(activeGrid.classes));
      return;
    }

    // Fallback for legacy grids: read the line_subject_config table.
    const timer = setTimeout(async () => {
      try {
        const { data: lineConfigData, error } = await supabase
          .from('line_subject_config')
          .select('*')
          .eq('grid_id', activeGrid.id)
          .single();

        if (error && !error.message?.includes('406')) {
          console.warn('Error loading line-subject configuration:', error);
        } else if (lineConfigData && lineConfigData.mappings && Array.isArray(lineConfigData.mappings) && lineConfigData.mappings.length > 0) {
          const availabilityMap = createSubjectAvailabilityMap(lineConfigData.mappings);
          setSubjectLineAvailability(availabilityMap);
        }
      } catch (error: any) {
        if (!error.message?.includes('406')) {
          console.error('Error loading line-subject configuration:', error);
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [activeGrid?.id, activeGrid?.classes]);

  // Teacher assignment handlers - FIXED: Use grid-specific assignment instead of activeGrid
  const handleTeacherAssignment = useCallback(async (classId: string, lineId: string, teacherCode: string | null) => {
    console.log('🔵 handleTeacherAssignment called:', { classId, lineId, teacherCode });

    // Find which grid contains this class (instead of relying on activeGrid)
    const targetGrid = allGrids.find(grid =>
      grid.classes.some(cls => cls.id === classId)
    );

    console.log('🔵 Found target grid:', targetGrid?.id, targetGrid?.name);

    if (!targetGrid) {
      console.error('❌ Target grid not found for classId:', classId);
      addToast('Failed to assign teacher: class not found', 'error');
      return;
    }

    // Capture the previous assignment for this cell so the action is undoable.
    const prevTeacher = targetGrid.classes.find(c => c.id === classId)?.lineAssignments?.[lineId] || '';

    try {
      // Use multi-grid assignment with specific grid context
      await assignTeacherMultiGrid(targetGrid.id, classId, lineId, teacherCode);
      addToast(
        `Teacher assigned in ${targetGrid.name}`,
        'success',
        6000,
        prevTeacher !== teacherCode
          ? {
              label: 'Undo',
              onClick: () => {
                assignTeacherMultiGrid(targetGrid.id, classId, lineId, prevTeacher)
                  .catch(() => addToast('Could not undo — please reassign manually', 'error'));
              },
            }
          : undefined
      );
    } catch (error) {
      console.error('❌ Error in handleTeacherAssignment:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        classId,
        lineId,
        teacherCode,
        gridId: targetGrid.id
      });
      addToast('Failed to assign teacher', 'error');
    }
  }, [allGrids, assignTeacherMultiGrid, addToast]); // ✅ FIXED: No longer depends on activeGrid

  // Cross-grid teacher assignment
  const handleCrossGridTeacherAssignment = useCallback(async (
    classId: string, 
    lineId: string, 
    teacherCode: string, 
    gridId: string
  ) => {
    try {
      if (assignTeacherMultiGrid) {
        await assignTeacherMultiGrid(gridId, classId, lineId, teacherCode);
      } else {
        await assignTeacher(classId, lineId, teacherCode);
      }
      addToast('Teacher assignment updated', 'success');
    } catch (error) {
      console.error('Error assigning teacher:', error);
      addToast('Failed to assign teacher', 'error');
    }
  }, [assignTeacherMultiGrid, assignTeacher, addToast]);

  // Class update handler - FIXED: Use grid-specific update instead of activeGrid
  const handleClassUpdate = useCallback(async (classId: string, updates: Partial<ClassAllocation>) => {
    // Find which grid contains this class (instead of relying on activeGrid)
    const targetGrid = allGrids.find(grid => 
      grid.classes.some(cls => cls.id === classId)
    );
    
    if (!targetGrid) {
      addToast('Failed to update class: class not found', 'error');
      return;
    }
    
    try {
      const updatedClasses = targetGrid.classes.map(c => 
        c.id === classId ? { ...c, ...updates } : c
      );
      await updateStabilizedGrid(targetGrid.id, { classes: updatedClasses });
      addToast(`Class updated in ${targetGrid.name}`, 'success');
    } catch (error) {
      console.error('Error updating class:', error);
      addToast('Failed to update class', 'error');
    }
  }, [allGrids, updateStabilizedGrid, addToast]); // ✅ FIXED: No longer depends on activeGrid

  // Toggle sidebar visibility
  const handleToggleSidebar = useCallback(() => {
    setSidebarVisible(!sidebarVisible);
    addToast(sidebarVisible ? 'Sidebar hidden' : 'Sidebar shown', 'info');
  }, [sidebarVisible, setSidebarVisible, addToast]);

  // Create new grid
  const handleCreateNewGrid = useCallback(async () => {
    // Validation: Grid name
    if (!newGridName.trim()) {
      addToast('Please enter a grid name', 'error');
      return;
    }

    // Validation: Minutes per class
    if (!newGridMinutes || newGridMinutes <= 0) {
      addToast('Please enter valid minutes per class (must be greater than 0)', 'error');
      return;
    }

    // Validation: Study periods exist
    if (!newGridStudyPeriods || newGridStudyPeriods.length === 0) {
      addToast('Please add at least one study period', 'error');
      return;
    }

    // Validation: Each study period has lines
    const emptyPeriods = newGridStudyPeriods.filter(period => !period.lines || period.lines.length === 0);
    if (emptyPeriods.length > 0) {
      addToast(`Study period "${emptyPeriods[0].name}" has no lines assigned. Please add at least one line to each study period.`, 'error');
      return;
    }

    // Validation: Check for duplicate line IDs across study periods
    const allLineIds: string[] = [];
    const duplicates: string[] = [];
    newGridStudyPeriods.forEach(period => {
      period.lines.forEach(lineId => {
        if (allLineIds.includes(lineId)) {
          if (!duplicates.includes(lineId)) {
            duplicates.push(lineId);
          }
        }
        allLineIds.push(lineId);
      });
    });

    if (duplicates.length > 0) {
      addToast(`Warning: Line(s) ${duplicates.join(', ')} appear in multiple study periods. This may cause conflicts.`, 'warning');
      // Don't return - just warn, as this might be intentional
    }

    // Extract all unique lines from all study periods
    const allLines = [...new Set(newGridStudyPeriods.flatMap(period => period.lines))];

    if (allLines.length === 0) {
      addToast('Please add at least one line to your grid', 'error');
      return;
    }

    try {
      console.log(`🎯 Creating new grid: "${newGridName}"`);
      console.log(`   - Minutes per class: ${newGridMinutes}`);
      console.log(`   - Total lines: ${allLines.length} (${allLines.join(', ')})`);
      console.log(`   - Study periods: ${newGridStudyPeriods.length}`);

      const gridId = await createGrid(newGridName, newGridMinutes, allLines, newGridStudyPeriods);

      if (!gridId) {
        throw new Error('Grid creation returned no ID');
      }

      console.log(`✅ Grid created successfully with ID: ${gridId}`);

      // Post-creation verification: Wait a moment for real-time subscription to update
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify the grid appears in the list
      const gridExists = allGrids.some(g => g.id === gridId);
      if (!gridExists) {
        console.warn('⚠️ Grid created but not yet visible in list - real-time sync may be delayed');
        // Grid will appear when real-time subscription updates
      } else {
        console.log('✅ Grid verified in list');
      }

      // Set as current grid and close modal
      setCurrentGridId(gridId);
      setShowCreateGrid(false);

      // Reset form
      setNewGridName('');
      setNewGridMinutes(70);
      setNewGridStudyPeriods([
        { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
        { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
      ]);
      setLineInputValues({});

      // Show detailed success message with verification note
      const verificationNote = gridExists
        ? 'Grid is now active and ready to use.'
        : 'Grid created successfully and will appear in the list momentarily.';

      addToast(
        `Grid "${newGridName}" created successfully! ` +
        `${allLines.length} lines configured across ${newGridStudyPeriods.length} study period(s). ` +
        `Default class duration: ${newGridMinutes} minutes. ${verificationNote}`,
        'success'
      );

      console.log('✅ Grid creation process completed successfully');
    } catch (error) {
      console.error('❌ Error creating grid:', error);

      // Provide more specific error message
      let errorMessage = 'Failed to create grid';
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }

      addToast(errorMessage, 'error');
    }
  }, [newGridName, newGridMinutes, newGridStudyPeriods, createGrid, setCurrentGridId, addToast, allGrids]);

  // Conflict detection
  const cachedConflictDetection = useMemo(() => {
    if (!activeGrid) return { conflicts: [], summary: { totalConflicts: 0, openConflicts: 0, criticalConflicts: 0, conflictsByType: {} } };
    
    const detectedConflicts = detectConflicts(activeGrid, staff, activeSemester, workingYear);
    const conflictSummary = generateConflictSummary(detectedConflicts);
    
    return { conflicts: detectedConflicts, summary: conflictSummary };
  }, [activeGrid?.classes, activeGrid?.lines, staff?.length, activeSemester]);

  const handleDetectConflicts = useCallback(() => {
    if (!activeGrid) return;
    
    const { conflicts: detectedConflicts, summary: conflictSummary } = cachedConflictDetection;
    
    if (conflictSummary.totalConflicts === 0) {
      addToast('No conflicts found in your timetable!', 'success');
    } else {
      addToast(`Found ${conflictSummary.totalConflicts} conflicts (${conflictSummary.criticalConflicts} critical)`, 'warning');
      navigate('/grid/conflicts', { 
        state: { 
          conflicts: detectedConflicts,
          gridId: activeGrid.id,
          gridName: activeGrid.name,
          activeSemester 
        } 
      });
    }
  }, [cachedConflictDetection, activeGrid, addToast, navigate, activeSemester]);

  // Export grid functionality
  const handleExportGrid = useCallback(() => {
    if (!activeGrid) {
      addToast('No active grid to export', 'error');
      return;
    }
    
    try {
      // Create CSV content
      const headers = ['Class', 'Learning Area', 'Year Level', ...activeGrid.lines.map(line => line.name)];
      const rows = activeGrid.classes.map(classItem => {
        const row = [
          classItem.className,
          displayLearningArea(classItem.curriculumArea, ''),
          classItem.yearLevel?.toString() || '',
        ];
        
        activeGrid.lines.forEach(line => {
          const teacher = classItem.lineAssignments?.[line.id] || '';
          row.push(teacher);
        });
        
        return row;
      });
      
      const csvContent = [headers, ...rows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeGrid.name.replace(/\s+/g, '_')}_export.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      addToast(`Grid exported successfully as CSV`, 'success');
    } catch (error) {
      addToast('Error exporting grid', 'error');
    }
  }, [activeGrid, addToast]);

  // Timetabler hand-off workbook (Classes + RUAH sheets), in her layout
  const handleExportForKaren = useCallback(async () => {
    if (!activeGrid) {
      addToast('No active grid to export', 'error');
      return;
    }
    try {
      const { classRows, ruahRows } = await exportGridForKaren(grids, activeGrid.year);
      addToast(`Exported ${classRows} class rows and ${ruahRows} RUAH rows`, 'success');
    } catch (error) {
      addToast('Error exporting grid for Karen', 'error');
    }
  }, [activeGrid, grids, addToast]);

  // Auto-assign functionality
  const handleFindMatches = useCallback((type: 'all' | 'perfect' | 'personal' | 'curriculum') => {
    if (!activeGrid || !staff.length) {
      addToast('Missing required data for match analysis', 'error');
      return;
    }

    console.log(`🚀 EnhancedMultiGridEditor: Starting ${type} match finding...`);
    console.log(`🔍 Current data state:`, {
      activeGrid: activeGrid ? { id: activeGrid.id, name: activeGrid.name, classCount: activeGrid.classes?.length || 0 } : null,
      staffCount: staff.length,
      teachingIntentionsCount: teachingIntentions.length,
      curriculumSubjectsCount: curriculumSubjects.length,
      sampleCurriculumSubjects: curriculumSubjects.slice(0, 3).map(s => ({
        subjectName: s.subjectName,
        teacherCount: s.teacherPreferences?.length || 0
      }))
    });

    setPreferencesLoading(true);

    try {
      let matches: any[] = [];

      // Teacher load, from the real year-scoped engine. This was a stub returning
      // a flat 1000 free minutes for everyone, so the capacity gate below always
      // passed and auto-assign could overload anybody (issue #173).
      const getTeacherLoad = (teacherCode: string, semester: string) => {
        const load = getTeacherSemesterLoad(teacherCode, semester as 'semester1' | 'semester2');
        return {
          availableMinutes: load.availableMinutes,
          allocatedMinutes: load.allocatedMinutes,
        };
      };

      // Who taught what last year, from last year's grids. pit.staff_teaching_history
      // — the intended source — has never been populated, and the prior year's grids
      // are the real record anyway (1,633 assignments across the 2026 grids).
      const priorTeaching = buildPriorTeachingIndex(grids, planningYear - 1);

      // The grid being written must be the one on screen, and must belong to the
      // year being planned. activeGrid is seeded from the first grid by name
      // across ALL years, and there are two grids called "Main Grid" (2026 and
      // 2027) — so without this a run could target last year's grid.
      if (currentGrid && activeGrid && currentGrid.id !== activeGrid.id) {
        addToast('Still switching grids — try again in a moment', 'error');
        return;
      }
      if (activeGrid && activeGrid.year != null && activeGrid.year !== planningYear) {
        addToast(
          `That grid is for ${activeGrid.year}, but you are planning ${planningYear}. Auto-assign stopped.`,
          'error'
        );
        return;
      }

      if (type === 'all') {
        const gridClasses = activeGrid?.classes || [];
        matches = findAllMatches(gridClasses, staff, teachingIntentions, curriculumSubjects, getTeacherLoad, activeGrid?.lines, priorTeaching);
      } else if (type === 'perfect') {
        const gridClasses = activeGrid?.classes || [];
        matches = findPerfectMatches(gridClasses, staff, teachingIntentions, curriculumSubjects, getTeacherLoad, activeGrid?.lines, priorTeaching);
      } else if (type === 'personal') {
        const gridClasses = activeGrid?.classes || [];
        matches = findPersonalPreferenceMatches(gridClasses, staff, teachingIntentions, getTeacherLoad, activeGrid?.lines, priorTeaching, curriculumSubjects);
      } else if (type === 'curriculum') {
        console.log(`🔍 Calling findCurriculumPreferenceMatches with:`, {
          activeGrid: activeGrid ? { id: activeGrid.id, name: activeGrid.name } : null,
          gridClasses: activeGrid?.classes?.length || 0,
          gridType: typeof activeGrid,
          staffCount: staff.length,
          curriculumSubjectsCount: curriculumSubjects.length
        });

        // Extract classes array from activeGrid
        const gridClasses = activeGrid?.classes || [];
        console.log(`🔍 Grid classes structure:`, {
          classesType: typeof gridClasses,
          isArray: Array.isArray(gridClasses),
          classCount: gridClasses.length,
          sampleClass: gridClasses[0] ? {
            id: gridClasses[0].id,
            className: gridClasses[0].className,
            subject: gridClasses[0].subject || gridClasses[0].className
          } : null
        });

        // Debug: Show first few class IDs to understand the structure
        if (gridClasses.length > 0) {
          console.log(`🔍 Sample class IDs:`, gridClasses.slice(0, 3).map(c => ({
            id: c.id,
            className: c.className,
            subject: c.subject || c.className
          })));
        }

        matches = findCurriculumPreferenceMatches(gridClasses, staff, curriculumSubjects, getTeacherLoad, activeGrid?.lines, priorTeaching, teachingIntentions);
        console.log(`📊 Curriculum matches result:`, {
          matchCount: matches.length,
          sampleMatches: matches.slice(0, 3).map(m => ({
            teacher: m.teacherCode,
            subject: m.subjectName,
            class: m.className,
            line: m.lineId,
            reasons: m.reasons
          }))
        });
      }

      // Add unique IDs for selection tracking
      const matchesWithIds = matches.map((match, index) => ({
        ...match,
        id: `${match.classId}-${match.lineId}-${match.teacherCode}-${index}`
      }));

      setAllMatches(matchesWithIds);
      // Nothing pre-selected: the coordinator opts in to each assignment rather
      // than opting out of a screen of pre-ticked ones.
      setSelectedMatches(new Set());
      setAssignmentType(type);
      setShowAutoAssignConfirm(true);

      console.log(`✅ EnhancedMultiGridEditor: Found ${matches.length} ${type} matches`);
      addToast(`Found ${matches.length} ${type} matches`, 'success');
    } catch (error) {
      console.error(`❌ EnhancedMultiGridEditor: Error finding ${type} matches:`, error);
      addToast('Error finding matches', 'error');
    } finally {
      setPreferencesLoading(false);
    }
  }, [activeGrid, currentGrid, planningYear, grids, staff, teachingIntentions, curriculumSubjects, getTeacherSemesterLoad, addToast]);

  // Auto-assign selected matches
  const handleAutoAssignSelectedMatches = useCallback(async () => {
    if (!activeGrid || selectedMatches.size === 0) return;

    // Re-assert the target between finding and applying. The matches were built
    // against whatever grid was active when Find ran; if that changed since, these
    // writes would land somewhere else entirely.
    if (currentGrid && currentGrid.id !== activeGrid.id) {
      addToast('The open grid changed — find matches again before assigning', 'error');
      return;
    }
    if (activeGrid.year != null && activeGrid.year !== planningYear) {
      addToast(
        `That grid is for ${activeGrid.year}, but you are planning ${planningYear}. Nothing was assigned.`,
        'error'
      );
      return;
    }

    setIsAutoAssigning(true);

    try {
      const matchesToAssign = allMatches.filter(match => selectedMatches.has(match.id));

      // ONE write for the whole batch, not one per assignment. The per-assignment
      // path rewrites the entire grid's JSONB and refetches it every time — on a
      // 346-class grid that is a full rewrite per tick, and it left a half-applied
      // batch behind if it failed partway.
      await batchAssignTeachers(
        matchesToAssign.map(match => ({
          classId: match.classId,
          lineId: match.lineId,
          teacherCode: match.teacherCode,
        }))
      );

      addToast(
        `Assigned ${matchesToAssign.length} teacher${matchesToAssign.length === 1 ? '' : 's'}`,
        'success'
      );
      setShowAutoAssignConfirm(false);
      setSelectedMatches(new Set());
      setAllMatches([]);
    } catch (error) {
      // Say plainly that NOTHING was applied. The old code reported
      // "Successfully assigned N of M" as a success toast even when writes had
      // failed, logging the failures to the console where nobody would see them.
      console.error('Error during auto-assignment:', error);
      addToast(
        `Nothing was assigned — the batch failed to save. ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        'error'
      );
    } finally {
      setIsAutoAssigning(false);
    }
  }, [activeGrid, currentGrid, planningYear, selectedMatches, allMatches, batchAssignTeachers, addToast]);

  // Toggle match selection
  const handleToggleMatch = useCallback((matchId: string) => {
    setSelectedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(matchId)) {
        newSet.delete(matchId);
      } else {
        newSet.add(matchId);
      }
      return newSet;
    });
  }, []);

  // Toggle all matches
  const handleToggleAll = useCallback(() => {
    if (selectedMatches.size === allMatches.length) {
      setSelectedMatches(new Set()); // Deselect all
    } else {
      setSelectedMatches(new Set(allMatches.map(match => match.id))); // Select all
    }
  }, [selectedMatches.size, allMatches]);

  // Calculate grid stats with semester filtering
  const gridStats = useMemo(() => {
    // Use currentGrid from MultiGrid context if available, fallback to activeGrid from stabilized context
    const targetGrid = currentGrid || activeGrid;
    if (!targetGrid) return null;
    
    
    // For MultiGrid context, lines are simple strings, for stabilized context they may be objects
    const allLines = targetGrid.lines || [];
    const lineIds = allLines.map(line => typeof line === 'string' ? line : line.id);
    
    // Apply semester filtering if using stabilized context (which has semester info)
    let filteredLineIds = lineIds;
    if (activeSemester !== 'both' && targetGrid === activeGrid) {
      // Only apply semester filtering for the old stabilized context
      filteredLineIds = allLines
        .filter(line => typeof line === 'object' ? line.semester === activeSemester : true)
        .map(line => typeof line === 'string' ? line : line.id);
    }
    
    const totalPossibleAssignments = (targetGrid.classes?.length || 0) * filteredLineIds.length;
    const actualAssignments = (targetGrid.classes || []).reduce((count, classItem) => {
      return count + Object.entries(classItem.lineAssignments || {}).filter(([lineId, teacherCode]) => {
        if (!teacherCode) return false;
        if (activeSemester === 'both' || targetGrid === currentGrid) return true;
        // Only apply semester filtering for stabilized context
        const line = allLines.find(l => (typeof l === 'string' ? l : l.id) === lineId);
        return typeof line === 'object' ? line.semester === activeSemester : true;
      }).length;
    }, 0);

    const stats = {
      totalClasses: targetGrid.classes?.length || 0,
      totalLines: filteredLineIds.length,
      totalSemesters: targetGrid.studyPeriods?.length || targetGrid.semesters?.length || 0,
      actualAssignments,
      totalPossibleAssignments,
      completionPercentage: totalPossibleAssignments > 0 ? Math.round(actualAssignments / totalPossibleAssignments * 100) : 0,
      semesterFilter: activeSemester
    };
    
    return stats;
  }, [currentGrid, activeGrid, activeSemester]);

  // Helper functions for additional comments
  const getTeachingIntention = useCallback((staffInitial: string): TeachingIntention | undefined => {
    // Try exact match first
    let intention = teachingIntentions.find(intention => intention.staffId === staffInitial);
    
    // If no exact match and staffInitial is 3+ characters, try first 2 characters
    if (!intention && staffInitial && staffInitial.length >= 3) {
      const shortCode = staffInitial.substring(0, 2);
      intention = teachingIntentions.find(intention => intention.staffId === shortCode);
    }
    
    // If still no match and staffInitial is 2 characters, try looking for 3-character codes that start with it
    if (!intention && staffInitial && staffInitial.length === 2) {
      intention = teachingIntentions.find(intention => 
        intention.staffId && intention.staffId.startsWith(staffInitial)
      );
    }
    
    return intention;
  }, [teachingIntentions]);

  const hasAdditionalComments = useCallback((staffInitial: string): boolean => {
    const intention = getTeachingIntention(staffInitial);
    const hasComments = !!(intention?.additionalComments && intention.additionalComments.trim().length > 0);
    
    
    return hasComments;
  }, [getTeachingIntention]);

  const showAdditionalComments = useCallback((staffInitial: string, staffName: string) => {
    const intention = getTeachingIntention(staffInitial);
    if (intention?.additionalComments) {
      setCommentsModal({
        isOpen: true,
        staffName,
        staffInitial,
        comments: intention.additionalComments
      });
    }
  }, [getTeachingIntention]);

  const closeCommentsModal = useCallback(() => {
    setCommentsModal({
      isOpen: false,
      staffName: '',
      staffInitial: '',
      comments: ''
    });
  }, []);

  // Loading state
  if (gridLoading || multiGridLoading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-500 mx-auto mb-4"></div>
          <p className="text-ink-500">Loading grids...</p>
        </div>
      </div>
    );
  }

  // No active grid state
  if (!activeGrid && !currentGrid) {
    console.log('🔄 EnhancedMultiGridEditor: Rendering "No Active Grid" state');
    console.log('🔄 activeGrid:', activeGrid);
    console.log('🔄 currentGrid:', currentGrid);
    console.log('🔄 showCreateGrid:', showCreateGrid);
    return (
      <div className="min-h-screen bg-ink-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <PageHeader title="Enhanced Multi-Grid Editor" description="Assign teachers to classes across multiple grids" />
          <Card>
            <div className="p-12 text-center">
              <GridIcon className="h-12 w-12 text-ink-300 mx-auto" />
              <h3 className="mt-2 text-sm font-medium text-ink-900">No Active Grid</h3>
              <p className="mt-1 text-sm text-ink-500">
                Create a new grid structure to start assigning teachers.
              </p>
              <div className="mt-6 space-x-3">
                <Button variant="primary" onClick={() => {
                  console.log('🔄 Create New Grid button clicked in No Active Grid state!');
                  console.log('🔄 setShowCreateGrid function:', typeof setShowCreateGrid);
                  setShowCreateGrid(true);
                  console.log('🔄 showCreateGrid state should now be true');
                }}>
                  Create New Grid
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Create Grid Modal - Available in No Active Grid state */}
        <Modal 
          isOpen={showCreateGrid} 
          onClose={() => {
            setShowCreateGrid(false);
            setNewGridName('');
            setNewGridMinutes(70);
            setNewGridStudyPeriods([
              { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
              { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
            ]);
            setLineInputValues({});
          }} 
          title="Create New Grid"
          footer={
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => {
                setShowCreateGrid(false);
                setNewGridName('');
                setNewGridMinutes(70);
                setNewGridStudyPeriods([
                  { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
                  { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
                ]);
                setLineInputValues({});
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleCreateNewGrid} disabled={!newGridName.trim() || !newGridMinutes || newGridMinutes <= 0}>
                Create Grid
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            {/* Grid Name */}
            <div>
              <label htmlFor="gridName" className="block text-sm font-medium text-ink-700 mb-2">
                Grid Name
              </label>
              <input
                type="text"
                id="gridName"
                value={newGridName}
                onChange={(e) => setNewGridName(e.target.value)}
                placeholder="Enter grid name (e.g., '2025 Main Timetable')"
                className="w-full border border-ink-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
              />
            </div>
            
            {/* Minutes per Class */}
            <div>
              <label htmlFor="gridMinutes" className="block text-sm font-medium text-ink-700 mb-2">
                Minutes per Class
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  id="gridMinutes"
                  min="30"
                  max="180"
                  step="5"
                  value={newGridMinutes}
                  onChange={(e) => setNewGridMinutes(parseInt(e.target.value) || 70)}
                  className="w-full border border-ink-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewGridMinutes(60)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      newGridMinutes === 60 
                        ? 'bg-navy-500 text-white border-navy-500' 
                        : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                    }`}
                  >
                    60min
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewGridMinutes(70)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      newGridMinutes === 70 
                        ? 'bg-navy-500 text-white border-navy-500' 
                        : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                    }`}
                  >
                    70min
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewGridMinutes(90)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      newGridMinutes === 90 
                        ? 'bg-navy-500 text-white border-navy-500' 
                        : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                    }`}
                  >
                    90min
                  </button>
                </div>
              </div>
            </div>

            {/* Study Periods and Lines Configuration */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Study Periods & Lines
              </label>
              <p className="text-sm text-ink-500 mb-4">
                Configure your study periods and assign which lines run during each period.
              </p>
              
              <div className="space-y-4">
                {newGridStudyPeriods.map((period, index) => (
                  <div key={index} className="border border-ink-200 rounded-lg p-4">
                    <div className="flex gap-2 items-center mb-3">
                      <input
                        type="text"
                        value={period.name}
                        onChange={(e) => {
                          const updated = [...newGridStudyPeriods];
                          updated[index] = { ...period, name: e.target.value };
                          setNewGridStudyPeriods(updated);
                        }}
                        placeholder="Study period name"
                        className="flex-1 border border-ink-300 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                      />
                      {newGridStudyPeriods.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = newGridStudyPeriods.filter((_, i) => i !== index);
                            setNewGridStudyPeriods(updated);
                          }}
                          className="p-2 text-danger-fg hover:text-danger-fg hover:bg-danger-bg rounded"
                          title="Remove study period"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-ink-500 mb-1">
                        Lines for {period.name}
                      </label>
                      <input
                        type="text"
                        value={lineInputValues[index] ?? period.lines.join(',')}
                        onChange={(e) => {
                          // Update the input display value immediately
                          setLineInputValues({ ...lineInputValues, [index]: e.target.value });
                        }}
                        onBlur={(e) => {
                          // Parse and clean the input when focus leaves
                          const lines = e.target.value.split(',').map(line => line.trim()).filter(line => line.length > 0);
                          const updated = [...newGridStudyPeriods];
                          updated[index] = { ...period, lines };
                          setNewGridStudyPeriods(updated);
                          // Clear the temporary input value
                          const newInputValues = { ...lineInputValues };
                          delete newInputValues[index];
                          setLineInputValues(newInputValues);
                        }}
                        onKeyDown={(e) => {
                          // Allow comma and common editing keys
                          if (e.key === 'Enter') {
                            e.currentTarget.blur(); // Trigger onBlur to save changes
                          }
                        }}
                        placeholder="Enter lines separated by commas (e.g., A,B,C)"
                        className="w-full border border-ink-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                      />
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...newGridStudyPeriods];
                            updated[index] = { ...period, lines: ['A', 'B', 'C'] };
                            setNewGridStudyPeriods(updated);
                            // Clear any temporary input value for this field
                            const newInputValues = { ...lineInputValues };
                            delete newInputValues[index];
                            setLineInputValues(newInputValues);
                          }}
                          className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                        >
                          A,B,C
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...newGridStudyPeriods];
                            updated[index] = { ...period, lines: ['X', 'Y', 'Z'] };
                            setNewGridStudyPeriods(updated);
                            // Clear any temporary input value for this field
                            const newInputValues = { ...lineInputValues };
                            delete newInputValues[index];
                            setLineInputValues(newInputValues);
                          }}
                          className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                        >
                          X,Y,Z
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
                            const startIndex = index * 6;
                            const endIndex = Math.min(startIndex + 6, letters.length);
                            const updated = [...newGridStudyPeriods];
                            updated[index] = { ...period, lines: letters.slice(startIndex, endIndex) };
                            setNewGridStudyPeriods(updated);
                            // Clear any temporary input value for this field
                            const newInputValues = { ...lineInputValues };
                            delete newInputValues[index];
                            setLineInputValues(newInputValues);
                          }}
                          className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                        >
                          Auto
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => {
                    const nextNum = newGridStudyPeriods.length + 1;
                    setNewGridStudyPeriods([
                      ...newGridStudyPeriods,
                      { id: `period${nextNum}`, name: `Period ${nextNum}`, lines: [] }
                    ]);
                  }}
                  className="w-full py-3 text-sm text-navy-600 hover:text-navy-800 hover:bg-navy-50 border border-dashed border-navy-300 rounded-md transition-colors"
                >
                  + Add Study Period
                </button>
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setNewGridStudyPeriods([
                    { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C', 'D', 'E', 'F'] },
                    { id: 'semester2', name: 'Semester 2', lines: ['G', 'H', 'I', 'J', 'K', 'L'] }
                  ])}
                  className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
                >
                  Semesters (A-F, G-L)
                </button>
                <button
                  type="button"
                  onClick={() => setNewGridStudyPeriods([
                    { id: 'term1', name: 'Term 1', lines: ['A', 'B', 'C'] },
                    { id: 'term2', name: 'Term 2', lines: ['D', 'E', 'F'] },
                    { id: 'term3', name: 'Term 3', lines: ['G', 'H', 'I'] },
                    { id: 'term4', name: 'Term 4', lines: ['J', 'K', 'L'] }
                  ])}
                  className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
                >
                  4 Terms
                </button>
                <button
                  type="button"
                  onClick={() => setNewGridStudyPeriods([
                    { id: 'fullyear', name: 'Full Year', lines: ['A', 'B', 'C', 'D', 'E', 'F'] }
                  ])}
                  className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
                >
                  Full Year
                </button>
              </div>
            </div>
            
            {/* Preview */}
            <div className="bg-ink-50 border border-ink-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-4 h-4 bg-ok-solid rounded-full mt-0.5 flex-shrink-0"></div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-medium text-ink-900 mb-1">
                    Grid Configuration Preview
                  </h4>
                  <p className="text-sm text-ink-500 mb-3">
                    Your grid will be created with the following settings:
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start justify-between">
                      <span className="text-ink-500 font-medium">Class Duration:</span>
                      <span className="font-mono tabular-nums text-ink-900">{newGridMinutes} minutes</span>
                    </div>
                    <div>
                      <span className="text-ink-500 font-medium">Study Periods & Lines:</span>
                      <div className="mt-2 space-y-2">
                        {newGridStudyPeriods.map((period, index) => (
                          <div key={index} className="flex items-start justify-between pl-2 py-1 bg-white rounded border">
                            <span className="text-ink-700 font-medium">{period.name}:</span>
                            <span className="font-mono tabular-nums text-ink-900">
                              {period.lines.length > 0 ? period.lines.join(', ') : 'No lines'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-start justify-between border-t pt-2">
                      <span className="text-ink-500 font-medium">Total Lines:</span>
                      <span className="font-mono tabular-nums text-ink-900">
                        {[...new Set(newGridStudyPeriods.flatMap(p => p.lines))].join(', ') || 'None'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

      </div>
    );
  }

  // Determine what to render based on current selection
  const renderContent = () => {
    if (currentGridId === null && (multiGridView.activeGridIds?.length || 0) > 0) {
      // Combined view
      return (
        <CombinedGridView
          staff={staff}
          onAssignTeacher={handleCrossGridTeacherAssignment}
          className="w-full"
        />
      );
    } else if (activeGrid) {
      // Individual grid view
      return (
        <StaffAllocationGrid
          grid={activeGrid}
          staff={staff}
          teachingIntentions={teachingIntentions}
          curriculumSubjects={curriculumSubjects}
          subjectLineAvailability={subjectLineAvailability}
          staffFilter={selectedStaffFilter}
          onStaffFilterChange={setSelectedStaffFilter}
          onAssignTeacher={handleTeacherAssignment}
          onClassUpdate={handleClassUpdate}
          onGridUpdate={async (updates) => {
            if (!activeGrid) return;
            await updateStabilizedGrid(activeGrid.id, updates);
          }}
          activeSemester={activeSemester}
        />
      );
    }

    return (
      <div className="flex-1 flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-ink-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ink-900 mb-2">No Grid Selected</h3>
          <p className="text-ink-500">Select a grid from the tabs above to start editing.</p>
        </div>
      </div>
    );
  };

  // Main full-width layout (same as GridEditor)
  return (
    <>
      <div className={`${sidebarVisible ? 'ml-64' : ''} fixed bg-ink-50 flex flex-col transition-all duration-300`} style={{
        top: '112px',
        left: sidebarVisible ? '0' : '0',
        right: '0',
        bottom: '0',
        zIndex: sidebarVisible ? 10 : 20
      }}>
      {/* Header with grid stats */}
      <div className="bg-white border-b border-ink-200 px-6 py-2 flex-shrink-0 w-full">
        <div className="w-full">
          <PageHeader
            title={`Grid Editor - ${planningYear}`}
            description={
              (currentGrid || activeGrid)
                ? `${(currentGrid || activeGrid).name} • ${gridStats?.totalClasses || 0} classes • ${gridStats?.totalLines || 0} lines • ${gridStats?.actualAssignments || 0}/${gridStats?.totalPossibleAssignments || 0} assignments (${gridStats?.completionPercentage || 0}% complete)`
                : (multiGridView.activeGridIds?.length || 0) > 0
                ? `Combined View • ${multiGridView.activeGridIds?.length || 0} grids active`
                : "Timetable editor with multi-grid support"
            }
            actions={
              <div className="flex items-center space-x-3">
                {/* Action Buttons */}
                <div className="flex space-x-1">
                  <Button 
                    variant="outline" 
                    size="sm"
                    leftIcon={<SidebarIcon className="h-3 w-3" />} 
                    onClick={handleToggleSidebar} 
                    title={sidebarVisible ? 'Hide sidebar' : 'Show sidebar'}
                    className="text-xs py-1 px-2"
                  >
                    {sidebarVisible ? 'Hide' : 'Show'} Sidebar
                  </Button>
                  
                  <Button 
                    variant={showPreferencesDrawer ? "primary" : "outline"} 
                    size="sm"
                    leftIcon={<Heart className="h-3 w-3" />} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const currentValue = showPreferencesDrawer;
                      console.log(`🔥 Staff Preferences Button clicked: current value = ${currentValue}`);
                      if (!currentValue) {
                        loadPreferenceData(); // Lazy load preferences when opening drawer
                      }
                      debugSetShowPreferencesDrawer(!currentValue);
                    }}
                    title={showPreferencesDrawer ? 'Hide staff preferences' : 'Show staff preferences'}
                    className="text-xs py-1 px-2"
                  >
                    Staff Preferences
                  </Button>
                  
                  <Button 
                    variant={showLoadsDrawer ? "primary" : "outline"} 
                    size="sm"
                    leftIcon={<PanelRight className="h-3 w-3" />} 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const currentValue = showLoadsDrawer;
                      console.log(`🔥 Button clicked: current value = ${currentValue}`);
                      if (!currentValue) {
                        loadPreferenceData();
                      }
                      debugSetShowLoadsDrawer(!currentValue);
                    }}
                    title={showLoadsDrawer ? 'Hide teacher loads' : 'Show teacher loads'}
                    className="text-xs py-1 px-2"
                  >
                    Teacher Loads
                  </Button>

                  {activeGrid && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        leftIcon={<AlertTriangle className="h-3 w-3" />} 
                        onClick={handleDetectConflicts}
                        className="text-xs py-1 px-2"
                      >
                        Check Conflicts
                      </Button>
                    </>
                  )}

                  {/* Auto-Assign Dropdown */}
                  {activeGrid && (
                    <div className="relative auto-assign-dropdown">
                      <Button 
                        variant="primary" 
                        size="sm"
                        leftIcon={<Check className="h-3 w-3" />}
                        rightIcon={<ChevronDown className="h-3 w-3" />}
                        onClick={() => setShowAutoAssignDropdown(!showAutoAssignDropdown)}
                        disabled={preferencesLoading}
                        title="Auto-assign teachers based on preferences"
                        className="text-xs py-1 px-2"
                      >
                        Auto-Assign
                      </Button>
                      {showAutoAssignDropdown && (
                        <div className="absolute right-0 mt-1 w-72 bg-white rounded-md shadow-lg border border-ink-200 z-50">
                          <div className="py-1">
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                              onClick={() => {
                                handleFindMatches('all');
                                setShowAutoAssignDropdown(false);
                              }}
                            >
                              <div className="flex items-center">
                                <Check className="h-4 w-4 mr-3 text-navy-600" />
                                <div>
                                  <div className="font-medium">All rules</div>
                                  <div className="text-xs text-ink-500">
                                    Everything below, ranked by priority
                                  </div>
                                </div>
                              </div>
                            </button>

                            <div className="my-1 border-t border-ink-200" />
                            <div className="px-4 pt-1 pb-1 text-[11px] uppercase tracking-wide text-ink-500">
                              Or one rule at a time
                            </div>

                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                              onClick={() => {
                                handleFindMatches('perfect');
                                setShowAutoAssignDropdown(false);
                              }}
                              disabled={!teachingIntentions.length || !curriculumSubjects.length}
                            >
                              <div className="flex items-center space-x-2 w-full">
                                <div className="h-2 w-2 bg-ok-solid rounded-full"></div>
                                <div className="flex-1">
                                  <div className="font-medium">Perfect Matches</div>
                                  <div className="text-xs text-ink-500">Self + curriculum nominated</div>
                                </div>
                              </div>
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                              onClick={() => {
                                handleFindMatches('personal');
                                setShowAutoAssignDropdown(false);
                              }}
                              disabled={!teachingIntentions.length}
                            >
                              <div className="flex items-center space-x-2 w-full">
                                <div className="h-2 w-2 bg-navy-500 rounded-full"></div>
                                <div className="flex-1">
                                  <div className="font-medium">Personal Preferences</div>
                                  <div className="text-xs text-ink-500">Teacher self-nominated</div>
                                </div>
                              </div>
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                              onClick={() => {
                                handleFindMatches('curriculum');
                                setShowAutoAssignDropdown(false);
                              }}
                              disabled={!curriculumSubjects.length}
                            >
                              <div className="flex items-center space-x-2 w-full">
                                <div className="h-2 w-2 bg-navy-500 rounded-full"></div>
                                <div className="flex-1">
                                  <div className="font-medium">Curriculum Preferences</div>
                                  <div className="text-xs text-ink-500">Leader nominated</div>
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* More Actions Dropdown */}
                  <div className="relative">
                    <Button 
                      variant="outline" 
                      size="sm"
                      leftIcon={<MoreHorizontal className="h-3 w-3" />}
                      onClick={() => setShowMoreActionsDropdown(!showMoreActionsDropdown)}
                      className="text-xs py-1 px-2"
                    >
                      More
                    </Button>
                    
                    {showMoreActionsDropdown && (
                      <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 min-w-48">
                        <div className="py-1">
                          <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                            onClick={() => {
                              navigate('/grid/import');
                              setShowMoreActionsDropdown(false);
                            }}
                          >
                            <UploadCloud className="h-4 w-4 mr-2" />
                            Import Subjects
                          </button>
                          <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                            onClick={() => {
                              setShowGridSettings(true);
                              setShowMoreActionsDropdown(false);
                            }}
                          >
                            <Layers className="h-4 w-4 mr-2" />
                            Grid Settings
                          </button>
                          {activeGrid && (
                            <>
                              <div className="border-t border-ink-100 my-1"></div>
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                                onClick={() => {
                                  handleExportGrid();
                                  setShowMoreActionsDropdown(false);
                                }}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Export
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 text-sm hover:bg-ink-100 flex items-center"
                                onClick={() => {
                                  handleExportForKaren();
                                  setShowMoreActionsDropdown(false);
                                }}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                Export for Karen
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </div>

      {/* Multi-Grid Tabs */}
      <MultiGridTabs
        staff={staff}
        onTabChange={(gridId) => {
          // Tab change handling is done internally by MultiGridTabs
        }}
        onCreateGrid={() => {
          setShowCreateGrid(true);
        }}
        onEditGrid={(gridId) => {
          // Set the grid as current so it shows in the settings modal
          setCurrentGridId(gridId);
          setShowGridSettings(true);
        }}
        className="flex-shrink-0"
      />


      {/* Main Content Area */}
      <div className="flex-1 relative overflow-y-auto">
        {renderContent()}

        {/* Staff Preferences Drawer */}
        {showPreferencesDrawer && (
          <div className={`absolute top-0 right-0 h-full bg-white border-l border-ink-200 shadow-lg transition-transform duration-300 ease-in-out z-30 w-96 ${
            showLoadsDrawer ? 'translate-x-96' : ''
          }`}>
            <div className="flex items-center justify-between p-4 border-b bg-ink-50">
              <h3 className="font-medium text-ink-900 flex items-center">
                <Heart className="h-4 w-4 mr-2" />
                Staff Preferences
              </h3>
              <button
                onClick={() => debugSetShowPreferencesDrawer(false)}
                className="p-1 hover:bg-ink-200 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="h-full overflow-y-auto pb-16">
            {/* Search Bar */}
            <div className="p-4 border-b bg-white sticky top-0 z-10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search staff name or subject preferences..."
                  value={preferencesSearchQuery}
                  onChange={(e) => setPreferencesSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-ink-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                />
                {preferencesSearchQuery && (
                  <button
                    onClick={() => setPreferencesSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-ink-100 rounded"
                  >
                    <X className="h-3 w-3 text-ink-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Staff Preferences Content */}
            <div className="p-4 space-y-4">
              {(() => {
                // Fuzzy matching function for subject names
                const calculateFuzzyScore = (str1: string, str2: string): number => {
                  if (!str1 || !str2) return 0;
                  
                  // Normalize strings (remove common words, punctuation, and standardize)
                  const normalize = (str: string) => {
                    return str
                      .toLowerCase()
                      .replace(/[^\w\s]/g, ' ') // Remove punctuation
                      .replace(/\b(discover|engage|explore|focus|vce|unit|units|introduction|intro|to|and|&|the|of|in|for|with)\b/g, '') // Remove common words
                      .replace(/\s+/g, ' ') // Normalize whitespace
                      .trim();
                  };
                  
                  const norm1 = normalize(str1);
                  const norm2 = normalize(str2);
                  
                  // Exact match after normalization
                  if (norm1 === norm2 && norm1.length > 0) return 100;
                  
                  // Check if one contains the other (after normalization)
                  if (norm1.length > 0 && norm2.length > 0) {
                    if (norm1.includes(norm2) || norm2.includes(norm1)) return 85;
                  }
                  
                  // Word overlap scoring
                  const words1 = norm1.split(' ').filter(w => w.length > 2);
                  const words2 = norm2.split(' ').filter(w => w.length > 2);
                  
                  if (words1.length === 0 || words2.length === 0) return 0;
                  
                  let commonWords = 0;
                  for (const word1 of words1) {
                    for (const word2 of words2) {
                      // Exact word match
                      if (word1 === word2) {
                        commonWords += 2;
                      }
                      // Partial word match (for abbreviations like "PE" vs "Physical Education")
                      else if (word1.length >= 3 && word2.length >= 3) {
                        if (word1.includes(word2) || word2.includes(word1)) {
                          commonWords += 1;
                        }
                      }
                      // Check for common abbreviations
                      else if ((word1 === 'pe' || word1 === 'hpe') && (word2.includes('physical') || word2.includes('health'))) {
                        commonWords += 2;
                      }
                      else if ((word2 === 'pe' || word2 === 'hpe') && (word1.includes('physical') || word1.includes('health'))) {
                        commonWords += 2;
                      }
                    }
                  }
                  
                  const totalWords = Math.max(words1.length, words2.length);
                  const score = (commonWords / totalWords) * 100;
                  
                  return Math.min(score, 100);
                };

                // Enhanced preference calculation with fuzzy matching
                const calculateEnhancedPreference = (staffCode: string, subjectName: string, intentions: any, subjectArea?: string) => {
                  let bestMatch = calculatePreferenceStrength(staffCode, subjectName, teachingIntentions, curriculumSubjects, subjectArea);
                  
                  // If we have teaching intentions, check for fuzzy matches with specific preferences
                  if (intentions && intentions.teachingPreferences) {
                    for (let i = 0; i < intentions.teachingPreferences.length; i++) {
                      const preference = intentions.teachingPreferences[i];

                      // Handle both string format (from database) and object format (legacy)
                      const preferenceText = typeof preference === 'string'
                        ? preference
                        : (preference.specificSubject || preference.learningArea);

                      if (!preferenceText) continue;

                      const fuzzyScore = calculateFuzzyScore(preferenceText, subjectName);

                      if (fuzzyScore >= 70) { // High similarity threshold
                        // This is a fuzzy match! Boost the score significantly
                        const enhancedMatch = {
                          teacherCode: staffCode,
                          teacherName: intentions.staffName || staffCode,
                          strength: 'strong' as const, // Upgrade to strong for good fuzzy matches
                          reasons: [
                            `Fuzzy match: "${preferenceText}" (${Math.round(fuzzyScore)}% similarity)`,
                            `Ranked #${i + 1} teaching preference`,
                            'Direct subject preference match'
                          ],
                          selfNominated: true,
                          curriculumNominated: false,
                          selfPreferenceRank: typeof preference === 'object' ? preference.rank : i + 1,
                          matchScore: Math.min(95 - ((typeof preference === 'object' ? preference.rank : i + 1) * 2), 90) // Higher score for better rank
                        };
                        
                        // Use the enhanced match if it's better than the original
                        if (enhancedMatch.matchScore > bestMatch.matchScore) {
                          bestMatch = enhancedMatch;
                        }
                      }
                    }
                  }
                  
                  return bestMatch;
                };

                // Filter staff based on search query
                const filteredStaff = staff.filter(staffMember => {
                  if (!preferencesSearchQuery.trim()) return true;
                  
                  const query = preferencesSearchQuery.toLowerCase();
                  const staffCode = staffMember.initial || staffMember.code;
                  
                  // Try exact match first, then try partial match (first 2 characters)
                  let intentions = teachingIntentions.find(ti => ti.staffId === staffCode);
                  if (!intentions && staffCode && staffCode.length > 2) {
                    const shortCode = staffCode.substring(0, 2);
                    intentions = teachingIntentions.find(ti => ti.staffId === shortCode);
                  }
                  if (!intentions && staffMember.name) {
                    intentions = teachingIntentions.find(ti => 
                      ti.staffName && 
                      ti.staffName.toLowerCase() === staffMember.name.toLowerCase()
                    );
                  }
                  
                  // Get all classes from the grid to generate preferences for
                  // Carry each class's learning area through with its name — the
                  // matcher needs it to score a teacher's area nominations (issue #168).
                  const classSubjects = (activeGrid?.classes ?? [])
                    .map(c => ({ subjectName: c.className, curriculumArea: c.curriculumArea }))
                    .filter((c, i, arr) => arr.findIndex(o => o.subjectName === c.subjectName) === i);
                  
                  // Calculate preference matches for search using enhanced fuzzy matching
                  const actualStaffId = intentions?.staffId || staffCode;
                  const preferenceMatches = classSubjects.map(({ subjectName, curriculumArea }) => {
                    const match = calculateEnhancedPreference(actualStaffId, subjectName, intentions, curriculumArea);
                    return {
                      subjectName,
                      ...match
                    };
                  }).filter(match => match.strength !== 'weak' && match.strength !== 'avoid');
                  
                  // Search in staff name, code, employment status, or subject preferences
                  const matchesName = staffMember.name?.toLowerCase().includes(query) || false;
                  const matchesCode = staffCode?.toLowerCase().includes(query) || false;
                  const matchesDepartment = staffMember.department?.toLowerCase().includes(query) || false;
                  const matchesStatus = intentions?.desiredEmploymentStatus?.toLowerCase().includes(query) || false;
                  const matchesSubjects = preferenceMatches.some(match => 
                    match.subjectName?.toLowerCase().includes(query) ||
                    match.reasons?.some(reason => reason?.toLowerCase().includes(query))
                  );
                  
                  return matchesName || matchesCode || matchesDepartment || matchesStatus || matchesSubjects;
                });

                if (filteredStaff.length === 0 && preferencesSearchQuery.trim()) {
                  return (
                    <div className="text-center py-8">
                      <Search className="h-12 w-12 text-ink-300 mx-auto mb-4" />
                      <h3 className="text-sm font-medium text-ink-900 mb-2">No matching staff found</h3>
                      <p className="text-sm text-ink-500">
                        Try searching by name, code, learning area, or subject preferences
                      </p>
                      <button
                        onClick={() => setPreferencesSearchQuery('')}
                        className="mt-3 text-sm text-navy-600 hover:text-navy-800"
                      >
                        Clear search
                      </button>
                    </div>
                  );
                }

                return filteredStaff.map(staffMember => {
                  const staffCode = staffMember.initial || staffMember.code;
                  
                  // Try exact match first, then try partial match (first 2 characters)
                  let intentions = teachingIntentions.find(ti => ti.staffId === staffCode);
                  if (!intentions && staffCode && staffCode.length > 2) {
                    // Try matching with first 2 characters
                    const shortCode = staffCode.substring(0, 2);
                    intentions = teachingIntentions.find(ti => ti.staffId === shortCode);
                  }
                  
                  // Also try finding by name match as fallback
                  if (!intentions && staffMember.name) {
                    intentions = teachingIntentions.find(ti => 
                      ti.staffName && 
                      ti.staffName.toLowerCase() === staffMember.name.toLowerCase()
                    );
                  }
                  
                  // Get all classes from the grid to generate preferences for
                  // Carry each class's learning area through with its name — the
                  // matcher needs it to score a teacher's area nominations (issue #168).
                  const classSubjects = (activeGrid?.classes ?? [])
                    .map(c => ({ subjectName: c.className, curriculumArea: c.curriculumArea }))
                    .filter((c, i, arr) => arr.findIndex(o => o.subjectName === c.subjectName) === i);
                  
                  // Calculate preference matches for each subject using enhanced fuzzy matching
                  const actualStaffId = intentions?.staffId || staffCode;
                  const preferenceMatches = classSubjects.map(({ subjectName, curriculumArea }) => {
                    const match = calculateEnhancedPreference(actualStaffId, subjectName, intentions, curriculumArea);
                    
                    return {
                      subjectName,
                      ...match
                    };
                  }).filter(match => match.strength !== 'weak' && match.strength !== 'avoid') // Only show rated preferences
                    .sort((a, b) => b.matchScore - a.matchScore); // Sort by match score (best first)
                  
                  // Highlight matching terms in subject names
                  const highlightText = (text: string | undefined, query: string) => {
                    if (!text || !query.trim()) return text || '';
                    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                    return text.replace(regex, '<mark class="bg-warn-bg px-0.5 rounded">$1</mark>');
                  };
                  
                  return (
                    <div key={staffMember.id} className="border rounded-lg p-3 bg-ink-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 
                            className="font-medium text-ink-900"
                            dangerouslySetInnerHTML={{ 
                              __html: highlightText(staffMember.name, preferencesSearchQuery) 
                            }}
                          />
                          {hasAdditionalComments(staffCode) && (
                            <button
                              onClick={() => showAdditionalComments(staffCode, staffMember.name)}
                              className="p-1 hover:bg-navy-100 rounded-full transition-colors group"
                              title="View additional comments"
                            >
                              <MessageSquare className="h-4 w-4 text-navy-600 group-hover:text-navy-700" />
                            </button>
                          )}
                        </div>
                        {staffCode && (
                          <span
                            className="font-mono tabular-nums text-sm text-ink-500"
                            dangerouslySetInnerHTML={{
                              __html: highlightText(staffCode, preferencesSearchQuery)
                            }}
                          />
                        )}
                      </div>
                      
                      {staffMember.department && (
                        <div className="mb-2 text-xs text-ink-500">
                          Learning Area: <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(displayLearningArea(staffMember.department), preferencesSearchQuery)
                            }}
                          />
                        </div>
                      )}
                      
                      {/* What the teacher actually wrote on their form. This must come
                          FIRST and be clearly separated from PIT's suggestions below:
                          the panel used to show only the computed matches under the
                          heading "Teaching Preferences", so a coordinator checking
                          whether someone had asked for a subject was shown an inferred
                          match and read it as a stated request (issue #182). */}
                      <StatedNominations intention={intentions} />

                      {preferenceMatches.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-ink-200">
                          <div className="text-sm font-medium text-ink-700">
                            Suggested by PIT ({preferenceMatches.length})
                          </div>
                          <div className="mb-2 text-xs text-ink-500">
                            Worked out from this year's grid — not what they asked for.
                          </div>
                          <div className="space-y-2">
                            {(() => {
                              const isExpanded = expandedStaffPreferences.has(staffMember.id);
                              const displayCount = isExpanded ? preferenceMatches.length : Math.min(3, preferenceMatches.length);
                              const displayMatches = preferenceMatches.slice(0, displayCount);
                              
                              return (
                                <>
                                  {displayMatches.map((match, idx) => {
                                    // Create unique key to avoid React conflicts
                                    const uniqueKey = `${staffMember.id}-${match.subjectName}-${idx}-${match.matchScore}`;
                                    
                                    // Enhanced rating badges for fuzzy matches
                                    const isFuzzyMatch = match.reasons?.some(r => r && typeof r === 'string' && r.includes('Fuzzy match')) || false;
                                    const ratingBadge = match.strength === 'strong' && isFuzzyMatch
                                      ? { text: '★', class: 'bg-warn-bg text-warn-fg', title: 'Direct subject preference match (fuzzy)' }
                                      : match.strength === 'strong'
                                      ? { text: '#1', class: 'bg-ok-bg text-ok-fg', title: 'Both staff and leader nominated' }
                                      : { text: '#2', class: 'bg-navy-100 text-navy-800', title: 'Either staff or leader nominated' };
                                    
                                    return (
                                      <div key={uniqueKey} className="flex items-center justify-between p-2 bg-white rounded border border-ink-200">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-ink-900 break-words">
                                            {match.subjectName}
                                          </div>
                                          <div className="text-xs text-ink-500 mt-1">
                                            {match.reasons.slice(0, 2).map((reason, reasonIdx) => (
                                              <span key={reasonIdx} className="mr-2">
                                                • {reason}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <span
                                            className={`font-mono tabular-nums inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ratingBadge.class}`}
                                            title={ratingBadge.title}
                                          >
                                            {ratingBadge.text}
                                          </span>
                                          <div className="font-mono tabular-nums text-xs text-ink-400">
                                            {match.matchScore}%
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  
                                  {preferenceMatches.length > 3 && (
                                    <button
                                      onClick={() => {
                                        const newExpanded = new Set(expandedStaffPreferences);
                                        if (isExpanded) {
                                          newExpanded.delete(staffMember.id);
                                        } else {
                                          newExpanded.add(staffMember.id);
                                        }
                                        setExpandedStaffPreferences(newExpanded);
                                      }}
                                      className="text-sm text-navy-600 hover:text-navy-800 flex items-center"
                                    >
                                      {isExpanded ? 'Show less' : `Show ${preferenceMatches.length - 3} more`}
                                      <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {preferenceMatches.length === 0 && (
                        <div className="text-sm text-ink-500">
                          No specific preferences found for current subjects
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
        )}


        {/* Teacher Loads Drawer */}
        {showLoadsDrawer && (
          <div
            className={`absolute top-0 right-0 h-full bg-white border-l border-ink-200 shadow-lg transition-transform duration-300 ease-in-out z-40 w-96 enhanced-teacher-loads-panel`}
            style={{
              transform: 'translateX(0)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
          <div className="flex items-center justify-between p-4 border-b bg-ink-50">
            <h3 className="font-medium text-ink-900 flex items-center">
              <PanelRight className="h-4 w-4 mr-2" />
              Teacher Loads
            </h3>
            <button
              onClick={handleCloseLoadsDrawer}
              className="p-1 hover:bg-ink-200 rounded"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="h-full overflow-y-auto pb-16">
            <TeacherLoadPanel
              grid={activeGrid}
              staff={staff}
              activeSemester={activeSemester}
              allGrids={allGrids}
              teachingIntentions={teachingIntentions}
              curriculumSubjects={curriculumSubjects}
            />
          </div>
        </div>
        )}
      </div>

      <Modal
        isOpen={showCreateGrid} 
        onClose={() => {
          setShowCreateGrid(false);
          setNewGridName('');
          setNewGridMinutes(70);
          setNewGridStudyPeriods([
            { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
            { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
          ]);
          setLineInputValues({});
        }} 
        title="Create New Grid"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => {
              setShowCreateGrid(false);
              setNewGridName('');
              setNewGridMinutes(70);
              setNewGridStudyPeriods([
                { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
                { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
              ]);
              setLineInputValues({});
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateNewGrid} disabled={!newGridName.trim() || !newGridMinutes || newGridMinutes <= 0}>
              Create Grid
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Grid Name */}
          <div>
            <label htmlFor="gridName" className="block text-sm font-medium text-ink-700 mb-2">
              Grid Name
            </label>
            <input
              type="text"
              id="gridName"
              value={newGridName}
              onChange={(e) => setNewGridName(e.target.value)}
              placeholder="Enter grid name (e.g., '2025 Main Timetable')"
              className="w-full border border-ink-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
            />
          </div>
          
          {/* Minutes per Class */}
          <div>
            <label htmlFor="gridMinutes" className="block text-sm font-medium text-ink-700 mb-2">
              Minutes per Class
            </label>
            <div className="flex gap-3">
              <input
                type="number"
                id="gridMinutes"
                min="30"
                max="180"
                step="5"
                value={newGridMinutes}
                onChange={(e) => setNewGridMinutes(parseInt(e.target.value) || 70)}
                className="w-full border border-ink-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewGridMinutes(60)}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    newGridMinutes === 60 
                      ? 'bg-navy-500 text-white border-navy-500' 
                      : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                  }`}
                >
                  60min
                </button>
                <button
                  type="button"
                  onClick={() => setNewGridMinutes(70)}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    newGridMinutes === 70 
                      ? 'bg-navy-500 text-white border-navy-500' 
                      : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                  }`}
                >
                  70min
                </button>
                <button
                  type="button"
                  onClick={() => setNewGridMinutes(90)}
                  className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                    newGridMinutes === 90 
                      ? 'bg-navy-500 text-white border-navy-500' 
                      : 'bg-ink-100 text-ink-700 border-ink-300 hover:bg-ink-200'
                  }`}
                >
                  90min
                </button>
              </div>
            </div>
          </div>

          {/* Study Periods and Lines Configuration */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Study Periods & Lines
            </label>
            <p className="text-sm text-ink-500 mb-4">
              Configure your study periods and assign which lines run during each period.
            </p>
            
            <div className="space-y-4">
              {newGridStudyPeriods.map((period, index) => (
                <div key={index} className="border border-ink-200 rounded-lg p-4">
                  <div className="flex gap-2 items-center mb-3">
                    <input
                      type="text"
                      value={period.name}
                      onChange={(e) => {
                        const updated = [...newGridStudyPeriods];
                        updated[index] = { ...period, name: e.target.value };
                        setNewGridStudyPeriods(updated);
                      }}
                      placeholder="Study period name"
                      className="flex-1 border border-ink-300 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                    {newGridStudyPeriods.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = newGridStudyPeriods.filter((_, i) => i !== index);
                          setNewGridStudyPeriods(updated);
                        }}
                        className="p-2 text-danger-fg hover:text-danger-fg hover:bg-danger-bg rounded"
                        title="Remove study period"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-ink-500 mb-1">
                      Lines for {period.name}
                    </label>
                    <input
                      type="text"
                      value={lineInputValues[index] ?? period.lines.join(',')}
                      onChange={(e) => {
                        // Update the input display value immediately
                        setLineInputValues({ ...lineInputValues, [index]: e.target.value });
                      }}
                      onBlur={(e) => {
                        // Parse and clean the input when focus leaves
                        const lines = e.target.value.split(',').map(line => line.trim()).filter(line => line.length > 0);
                        const updated = [...newGridStudyPeriods];
                        updated[index] = { ...period, lines };
                        setNewGridStudyPeriods(updated);
                        // Clear the temporary input value
                        const newInputValues = { ...lineInputValues };
                        delete newInputValues[index];
                        setLineInputValues(newInputValues);
                      }}
                      onKeyDown={(e) => {
                        // Allow comma and common editing keys
                        if (e.key === 'Enter') {
                          e.currentTarget.blur(); // Trigger onBlur to save changes
                        }
                      }}
                      placeholder="Enter lines separated by commas (e.g., A,B,C)"
                      className="w-full border border-ink-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                    <div className="flex gap-1 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [...newGridStudyPeriods];
                          updated[index] = { ...period, lines: ['A', 'B', 'C'] };
                          setNewGridStudyPeriods(updated);
                          // Clear any temporary input value for this field
                          const newInputValues = { ...lineInputValues };
                          delete newInputValues[index];
                          setLineInputValues(newInputValues);
                        }}
                        className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                      >
                        A,B,C
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [...newGridStudyPeriods];
                          updated[index] = { ...period, lines: ['X', 'Y', 'Z'] };
                          setNewGridStudyPeriods(updated);
                          // Clear any temporary input value for this field
                          const newInputValues = { ...lineInputValues };
                          delete newInputValues[index];
                          setLineInputValues(newInputValues);
                        }}
                        className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                      >
                        X,Y,Z
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
                          const startIndex = index * 6;
                          const endIndex = Math.min(startIndex + 6, letters.length);
                          const updated = [...newGridStudyPeriods];
                          updated[index] = { ...period, lines: letters.slice(startIndex, endIndex) };
                          setNewGridStudyPeriods(updated);
                          // Clear any temporary input value for this field
                          const newInputValues = { ...lineInputValues };
                          delete newInputValues[index];
                          setLineInputValues(newInputValues);
                        }}
                        className="px-2 py-1 text-xs bg-ink-100 text-ink-700 border border-ink-300 rounded hover:bg-ink-200"
                      >
                        Auto
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  const nextNum = newGridStudyPeriods.length + 1;
                  setNewGridStudyPeriods([
                    ...newGridStudyPeriods,
                    { id: `period${nextNum}`, name: `Period ${nextNum}`, lines: [] }
                  ]);
                }}
                className="w-full py-3 text-sm text-navy-600 hover:text-navy-800 hover:bg-navy-50 border border-dashed border-navy-300 rounded-md transition-colors"
              >
                + Add Study Period
              </button>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setNewGridStudyPeriods([
                  { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C', 'D', 'E', 'F'] },
                  { id: 'semester2', name: 'Semester 2', lines: ['G', 'H', 'I', 'J', 'K', 'L'] }
                ])}
                className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
              >
                Semesters (A-F, G-L)
              </button>
              <button
                type="button"
                onClick={() => setNewGridStudyPeriods([
                  { id: 'term1', name: 'Term 1', lines: ['A', 'B', 'C'] },
                  { id: 'term2', name: 'Term 2', lines: ['D', 'E', 'F'] },
                  { id: 'term3', name: 'Term 3', lines: ['G', 'H', 'I'] },
                  { id: 'term4', name: 'Term 4', lines: ['J', 'K', 'L'] }
                ])}
                className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
              >
                4 Terms
              </button>
              <button
                type="button"
                onClick={() => setNewGridStudyPeriods([
                  { id: 'fullyear', name: 'Full Year', lines: ['A', 'B', 'C', 'D', 'E', 'F'] }
                ])}
                className="px-3 py-1 text-sm bg-ink-100 text-ink-700 border border-ink-300 rounded-md hover:bg-ink-200"
              >
                Full Year
              </button>
            </div>
          </div>
          
          {/* Preview */}
          <div className="bg-ink-50 border border-ink-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              {(() => {
                const allLines = [...new Set(newGridStudyPeriods.flatMap(p => p.lines))];
                const hasEmptyPeriods = newGridStudyPeriods.some(p => !p.lines || p.lines.length === 0);
                const isValid = newGridName.trim() && newGridMinutes > 0 && allLines.length > 0 && !hasEmptyPeriods;

                return (
                  <>
                    <div className={`w-4 h-4 rounded-full mt-0.5 flex-shrink-0 ${isValid ? 'bg-ok-solid' : 'bg-warn-solid'}`}></div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-ink-900 mb-1">
                        Grid Configuration Preview
                      </h4>
                      <p className="text-sm text-ink-500 mb-3">
                        {isValid
                          ? 'Your grid is ready to be created with the following settings:'
                          : 'Please complete the configuration to create your grid:'}
                      </p>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-start justify-between">
                          <span className="text-ink-500 font-medium">Grid Name:</span>
                          <span className={`text-ink-900 ${!newGridName.trim() ? 'text-danger-fg italic' : ''}`}>
                            {newGridName.trim() || 'Not set'}
                          </span>
                        </div>
                        <div className="flex items-start justify-between">
                          <span className="text-ink-500 font-medium">Class Duration:</span>
                          <span className={`font-mono tabular-nums text-ink-900 ${newGridMinutes <= 0 ? 'text-danger-fg' : ''}`}>
                            {newGridMinutes} minutes
                          </span>
                        </div>
                        <div>
                          <span className="text-ink-500 font-medium">Study Periods & Lines:</span>
                          <div className="mt-2 space-y-2">
                            {newGridStudyPeriods.map((period, index) => (
                              <div key={index} className={`flex items-start justify-between pl-2 py-1 bg-white rounded border ${period.lines.length === 0 ? 'border-danger-border' : 'border-ink-200'}`}>
                                <span className="text-ink-700 font-medium">{period.name}:</span>
                                <span className={`font-mono tabular-nums ${period.lines.length > 0 ? 'text-ink-900' : 'text-danger-fg italic text-xs'}`}>
                                  {period.lines.length > 0 ? period.lines.join(', ') : 'No lines assigned'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-start justify-between border-t pt-2">
                          <span className="text-ink-500 font-medium">Total Unique Lines:</span>
                          <span className={`font-mono tabular-nums ${allLines.length > 0 ? 'text-ink-900 font-semibold' : 'text-danger-fg'}`}>
                            {allLines.length > 0 ? `${allLines.length} (${allLines.join(', ')})` : 'None'}
                          </span>
                        </div>

                        {!isValid && (
                          <div className="mt-3 pt-3 border-t border-warn-border">
                            <div className="flex items-start gap-2">
                              <span className="text-warn-fg text-xs">⚠️</span>
                              <div className="text-xs text-warn-fg">
                                {!newGridName.trim() && <div>• Grid name is required</div>}
                                {newGridMinutes <= 0 && <div>• Valid class duration is required</div>}
                                {allLines.length === 0 && <div>• At least one line must be configured</div>}
                                {hasEmptyPeriods && <div>• All study periods must have at least one line</div>}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </Modal>

      {/* Auto-Assign Confirmation Modal */}
      <Modal 
        isOpen={showAutoAssignConfirm} 
        onClose={() => setShowAutoAssignConfirm(false)} 
        title={`Auto-assign — ${assignmentType} matches for ${planningYear}`}
        size="xl"
        footer={
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="selectAll"
                checked={selectedMatches.size === allMatches.length && allMatches.length > 0}
                onChange={handleToggleAll}
                disabled={isAutoAssigning}
              />
              <label htmlFor="selectAll" className="text-sm text-ink-500">
                Select all ({allMatches.length} matches)
              </label>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setShowAutoAssignConfirm(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleAutoAssignSelectedMatches}
                disabled={isAutoAssigning || selectedMatches.size === 0}
                leftIcon={isAutoAssigning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              >
                {isAutoAssigning ? 'Assigning...' : `Assign ${selectedMatches.size} Match${selectedMatches.size !== 1 ? 'es' : ''}`}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {allMatches.length > 0 && (
            <div className="rounded-md border border-ink-200 bg-ink-50 p-3 text-sm text-ink-700">
              Listed best-first, using the school's priority order. Nothing is
              selected — tick the ones you want, then assign. Applying is a single
              save: it either all lands or none of it does.
            </div>
          )}
          {allMatches.length === 0 ? (
            <div className="text-center py-8">
              <Info className="h-12 w-12 text-ink-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink-900 mb-2">No matches found</h3>
              <p className="text-ink-500">
                No {assignmentType} matches were found for the current grid.
              </p>
            </div>
          ) : (
            allMatches.map((match, index) => (
              <div 
                key={match.id}
                className="flex items-center p-3 border rounded-md hover:bg-ink-50"
              >
                <input
                  type="checkbox"
                  id={`match-${match.id}`}
                  checked={selectedMatches.has(match.id)}
                  onChange={() => handleToggleMatch(match.id)}
                  disabled={isAutoAssigning}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-ink-900">
                        {match.teacherName} → {match.className}
                      </div>
                      {/* lineName and strength are not fields on a match — they
                          rendered as "undefined", hiding the one detail that
                          mattered: which line it would be written to (issue #173). */}
                      <div className="text-sm text-ink-500 font-mono tabular-nums">
                        Line {match.lineId} · {match.subjectName} · score {match.matchScore}
                      </div>
                      <div className="text-xs text-ink-500 mt-0.5">
                        <span className="font-mono tabular-nums font-medium">
                          Priority {match.priorityTier}
                        </span>
                        {' — '}
                        {PRIORITY_TIER_LABELS[match.priorityTier as keyof typeof PRIORITY_TIER_LABELS] ??
                          'outside the priority order'}
                      </div>
                      {match.reasons && (
                        <div className="text-xs text-ink-400 mt-1">
                          {match.reasons.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      match.strength === 'strong' ? 'bg-ok-bg text-ok-fg' :
                      match.strength === 'moderate' ? 'bg-navy-100 text-navy-800' :
                      'bg-ink-100 text-ink-900'
                    }`}>
                      {match.strength}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </Modal>

      </div>

      {showGridSettings && activeGrid && (
        <GridSettingsModal
          isOpen={showGridSettings}
          grid={activeGrid}
          onSaveChanges={async (updates) => {
            try {
              await updateGrid(activeGrid.id, updates);

              // Force a reload of preference data if lines changed
              if (updates.lines) {
                loadPreferenceData();
              }
              setShowGridSettings(false);
              addToast('Grid settings saved', 'success');
            } catch (error) {
              console.error('🌐 MultiGridEditor: Error saving grid changes:', error);
              console.error('🌐 MultiGridEditor: Error details:', error);
              addToast('Failed to save grid changes', 'error');
              throw error;
            }
          }}
          onClose={() => setShowGridSettings(false)}
        />
      )}

      <AdditionalCommentsModal
        isOpen={commentsModal.isOpen}
        onClose={closeCommentsModal}
        staffName={commentsModal.staffName}
        staffInitial={commentsModal.staffInitial}
        additionalComments={commentsModal.comments}
      />
    </>
  );
};

// Main component wrapped with MultiGridProvider (simplified pattern)
export default function EnhancedMultiGridEditor() {
  return (
    <MultiGridProvider>
      <EnhancedMultiGridEditorContent />
    </MultiGridProvider>
  );
}